// ─────────────────────────────────────────────
//  TestModeBanner — the permanently visible reminder that this is not today,
//  and the way out.
//
//  It names all THREE facts — facility · date · who you are — because a tester
//  who has forgotten which day they are standing in is the failure this whole
//  feature can cause. "Previewing as Ana" is not enough when the board is also
//  showing last Tuesday.
//
//  ⚠️ IT RENDERS ON NATIVE TOO, UNLIKE PreviewBanner. That banner hides itself
//  on the native shell because the mobile header already carries the "viewing
//  as" signal and its own Exit. Nothing carries THIS one: a phone in testing
//  mode would otherwise show a real-looking board from a past day with no
//  indication and no way back.
//
//  ⚠️ LEAVING DESTROYS THE STORE, NOT JUST THE FLAG. `sandbox/exit.js` exists
//  but effectively nothing calls it, and the sandbox is a one-way door as a
//  result. A cleared flag with a surviving store means the next entry silently
//  re-serves a stale day.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { readTestMode, testModeLabel } from "../../domain/testMode";
import { leaveTestMode } from "./leaveTestMode";
import tokens from "../../theme/tokens";

const { color } = tokens;

export default function TestModeBanner() {
  // Boot-time state: read once. It cannot change without a reload, and a
  // re-read on every render would be pure waste.
  const [cfg] = useState(() => readTestMode());
  const [leaving, setLeaving] = useState(false);

  if (!cfg) return null;

  const exit = async () => {
    setLeaving(true);
    try {
      await leaveTestMode();
      // leaveTestMode reloads; nothing after this runs.
    } catch (err) {
      console.error("[testing] could not leave cleanly:", err);
      setLeaving(false);
    }
  };

  return (
    <div style={s.bar} role="status" aria-live="polite" data-testid="test-mode-banner">
      <span aria-hidden="true" style={{ fontSize: 16 }}>🧪</span>
      <span style={s.text}>
        <strong>{testModeLabel(cfg)}</strong>
        {" — "}a local copy of that day. Nothing you change here is saved, and
        permission rules do not apply.
      </span>
      <button type="button" style={s.exit} onClick={exit} disabled={leaving}>
        {leaving ? "Leaving…" : "✕ Leave testing"}
      </button>
    </div>
  );
}

const s = {
  // Purple, deliberately not the amber of PreviewBanner: both can be on at once
  // (testing mode always previews somebody), and two identical bars would read
  // as one duplicated warning rather than two different facts.
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px",
    background: "#f3e8ff",
    borderBottom: "2px solid #a855f7",
    color: "#4a1d6e",
    fontSize: 13,
    lineHeight: 1.4,
    flexWrap: "wrap",
  },
  text: { flex: 1, minWidth: 0 },
  exit: {
    flexShrink: 0,
    // 44 is the audit floor, so budget above it — engines floor lengths to
    // 1/64px and an exact 44 renders 43.98.
    minHeight: 48,
    padding: "0 14px",
    border: `1px solid ${color.border.default}`,
    borderRadius: 6,
    background: "#fff",
    color: "#4a1d6e",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
  },
};
